import React from 'react'
import { Box, Typography, IconButton, useMediaQuery } from '@material-ui/core'
import classNames from 'classnames'
import { Meridiem } from '../Calendar'
import { Day as DayType } from '../Calendar'
import { format } from 'date-fns'
import classes from './MonthlyCalendar.styles'
import theme, { COLORS } from '../../../styles/theme'

interface MonthlyMatrixProps {
  fullDays: DayType[]
  currentDay: string
  currentMonth: string
  year: number
  selectedDayIndex: number
  selectedTimeIndex?: number
  selectedMeridiem?: Meridiem
  selectedDay?: DayType
  offset?: number
  changeTimeslot: (params: {
    day: DayType
    meridiem?: Meridiem
    timeIndex?: number
    onMobile?: boolean
  }) => Promise<void>
  onDayClick: (day: DayType, index: number) => void
}

const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export const MonthlyMatrix: React.FC<MonthlyMatrixProps> = ({
  fullDays,
  year,
  selectedDayIndex,
  selectedDay,
  changeTimeslot,
  onDayClick,
}) => {
  const today = new Date()
  const todayDate = today.getDate().toString()
  const todayMonth = format(today, 'LLL')
  const todayYear = today.getFullYear()
  const matrix: (DayType)[][] = []



  for (let i = 0; i < fullDays.length; i += 7) {
    matrix.push(fullDays.slice(i, i + 7))
  }

  const handleDayClick = (day: DayType, index: number) => {
    onDayClick(day, index)
    changeTimeslot({ day, onMobile: false })
  }

  const isPrevMonth = (day: DayType): boolean => {
    const dayDate = new Date(`${day.month} ${day.date}, ${year}`)
    const current = new Date()
    if (dayDate.getFullYear() < current.getFullYear()) return true
    if (
      dayDate.getFullYear() === current.getFullYear() &&
      dayDate.getMonth() < current.getMonth()
    )
      return true
    return false
  }

  const isAfter28Days = (day: DayType): boolean => {
    const today = new Date()
    const dayDate = new Date(`${day.month} ${day.date}, ${year}`)
    dayDate.setHours(0, 0, 0, 0)
    today.setHours(0, 0, 0, 0)
    const diffInMs = dayDate.getTime() - today.getTime()
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24)
    return diffInDays > 28
  }


  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Box display="flex" flexDirection="column" className={classes.matrixBorder} gridGap={8}>
      <Box display="flex" justifyContent="space-around">
        {weekDays.map((day) => (
          <Typography key={day} variant="caption" className={classes.weekDayInMonth}>
            {day.toUpperCase()}
          </Typography>
        ))}
      </Box>

      {matrix.map((week, weekIndex) => (
        <Box key={weekIndex} display="flex" justifyContent='space-around' height={'50px'}>
          {week.map((day, dayIndex) => {
            const globalIndex = weekIndex * 7 + dayIndex
            const isToday =
              day?.date === todayDate &&
              day?.month === todayMonth &&
              year === todayYear

            const isSelected =
              globalIndex === selectedDayIndex &&
              selectedDay?.date === day?.date &&
              selectedDay?.month === day?.month

            const blocked = isPrevMonth(day) || isAfter28Days(day)

            const textColor = blocked || !day.available
              ? '#C8C8C8'
              : COLORS.bodyTextPrimary

            return (
              <IconButton
                id={`day-${day?.month}${day?.date}`}
                key={day ? `${day.month}-${day.date}` : `empty-${weekIndex}-${dayIndex}`}
                className={classNames(classes.weekDay, {
                  currentDate: isToday,
                  selectedDay: isSelected,
                })}
                onClick={() => handleDayClick(day, globalIndex)}
                disabled={blocked}
                style={{
                  boxSizing: 'border-box',
                  width: !isMobile ? '48px' : undefined,
                }}
              >
                <Typography style={{ width: '26px', height: '27px', color: textColor }}>
                  {day?.date ?? ''}
                </Typography>
              </IconButton>

            )
          })}
        </Box>
      ))}
    </Box>
  )
}